/**
 * AttendanceSharedUI.jsx
 * Single source of truth for design tokens, shared components, and pure utilities.
 * Nothing here has side-effects — safe to tree-shake.
 */

import React from 'react';
import {
  Box, Typography, Avatar, IconButton, Chip, alpha,
  TextField, Select, MenuItem, FormControl, InputAdornment,
} from '@mui/material';
import { toStorageUrl } from '../../../utils/storageUrl';
import { motion } from 'framer-motion';
import {
  ACCENT, TEXT_PRIMARY, TEXT_SECONDARY,
  tableListShellSx, tableListHeaderBarSx,
} from '../../common/SaaSTokens';
import {
  ChevronLeft  as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

// ─── Palette ──────────────────────────────────────────────────────────────────
export const C = {
  // Backgrounds
  bg0: '#060d18',
  bg1: '#0b1525',
  bg2: '#111e33',
  bg3: '#162440',

  // Neutrals
  border:  'rgba(255,255,255,0.07)',
  border2: 'rgba(255,255,255,0.12)',
  textPrimary:   '#e2e8f0',
  textSecondary: '#64748b',
  textMuted:     '#334155',

  // Accent
  blue:   '#3b82f6',
  blueDim: '#1d4ed8',

  // Semantic
  green:  '#22c55e',
  amber:  '#f59e0b',
  red:    '#ef4444',
  purple: '#818cf8',
};

// ─── Typography scale ─────────────────────────────────────────────────────────
export const FONT_MONO  = "'JetBrains Mono', 'Fira Code', monospace";
export const FONT_UI    = "'DM Sans', 'Inter', sans-serif";
export const FONT_TITLE = "'Rajdhani', 'DM Sans', sans-serif";

// ─── Reusable sx ──────────────────────────────────────────────────────────────
export const R = {
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
};

export const TRANSITION = 'all 0.16s ease';

// ─── Glass card surface ───────────────────────────────────────────────────────
export const glassCardSx = {
  borderRadius: R.xl,
  background: `linear-gradient(160deg, ${alpha(C.bg1, 0.96)} 0%, ${alpha(C.bg0, 0.98)} 100%)`,
  border: `1px solid ${C.border}`,
  backdropFilter: 'blur(16px)',
  overflow: 'hidden',
};

// ─── Inner section surface ────────────────────────────────────────────────────
export const innerSurfaceSx = {
  borderRadius: R.lg,
  background: alpha(C.bg2, 0.5),
  border: `1px solid ${C.border}`,
};

// ─── Attendance record helpers ────────────────────────────────────────────────
export const normalizeAttendanceDate = (value) => {
  if (!value) return value;
  const raw = String(value).split('T')[0];
  const [year, month, day] = raw.split('-').map(Number);
  if (!year || !month || !day) return raw;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

export const sameAttendanceRecord = (record, userId, date) =>
  Number(record?.user_id) === Number(userId)
  && normalizeAttendanceDate(record?.date) === normalizeAttendanceDate(date);

// ─── Status metadata ──────────────────────────────────────────────────────────
export const getStatusMeta = (attendance) => {
  if (!attendance) return { type: 'no_record', label: 'No attendance yet', symbol: '○', fg: '#94a3b8', bg: alpha('#94a3b8', 0.12), border: alpha('#94a3b8', 0.32) };
  const { status, check_in_time } = attendance;
  const hasCheckIn = Boolean(check_in_time);
  // Explicit status overrides win over check-in (HR / team leader monthly corrections).
  if (status === 'approved_absent') {
    return {
      type: 'approved_absent',
      label: hasCheckIn ? 'Approved (override)' : 'Approved',
      symbol: '~',
      fg: C.amber,
      bg: alpha(C.amber, 0.1),
      border: alpha(C.amber, 0.22),
    };
  }
  if (status === 'absent') {
    return {
      type: 'absent',
      label: hasCheckIn ? 'Absent (override)' : 'Absent',
      symbol: '✗',
      fg: C.red,
      bg: alpha(C.red, 0.1),
      border: alpha(C.red, 0.22),
    };
  }
  if (status === 'present' || hasCheckIn) {
    return { type: 'present', label: 'Present', symbol: '✓', fg: C.green, bg: alpha(C.green, 0.1), border: alpha(C.green, 0.22) };
  }
  return { type: 'no_record', label: 'No attendance yet', symbol: '○', fg: '#94a3b8', bg: alpha('#94a3b8', 0.12), border: alpha('#94a3b8', 0.32) };
};

// ─── isUserPresent ────────────────────────────────────────────────────────────
export const isUserPresent = (record) => {
  if (!record) return false;
  if (record.status === 'approved_absent' || record.status === 'absent') return false;
  return record.status === 'present' || Boolean(record.check_in_time);
};

// ─── getDepartments ───────────────────────────────────────────────────────────
export const getDepartments = (users) => {
  const map = new Map();
  users.forEach((u) => {
    const id   = u.department?.id ?? u.department_id;
    const name = u.department?.name;
    if (id != null) map.set(String(id), name || `Dept ${id}`);
  });
  return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
};

// ─── StatusBadge ─────────────────────────────────────────────────────────────
export const StatusBadge = ({ attendance }) => {
  const meta = getStatusMeta(attendance);
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1.25, py: 0.45, borderRadius: 999,
      fontSize: 12, fontWeight: 600, lineHeight: 1.4,
      fontFamily: FONT_UI,
      bgcolor: meta.bg, color: meta.fg, border: `1px solid ${meta.border}`,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ fontSize: 10 }}>{meta.symbol}</span>
      {meta.label}
    </Box>
  );
};

// ─── UserAvatarCell ───────────────────────────────────────────────────────────
export const UserAvatarCell = ({ user, size = 36, maxWidth = 160 }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
    <Avatar
      src={user.profile_picture ? toStorageUrl(user.profile_picture_url || user.profile_picture) : undefined}
      alt={user.name}
      sx={{
        width: size, height: size, borderRadius: R.md, flexShrink: 0,
        bgcolor: alpha(C.blue, 0.12), color: C.blue,
        border: `1px solid ${alpha(C.blue, 0.18)}`,
        fontFamily: FONT_TITLE, fontWeight: 700, fontSize: size * 0.38,
      }}
    >
      {String(user.name || '?')[0].toUpperCase()}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{
        fontSize: 13.5, fontWeight: 600, color: C.textPrimary, fontFamily: FONT_UI,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth,
      }}>
        {user.name}
      </Typography>
      {user.matric_number && (
        <Typography sx={{
          fontSize: 11, color: C.textSecondary, fontFamily: FONT_MONO,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth,
          letterSpacing: 0.3,
        }}>
          {user.matric_number}
        </Typography>
      )}
    </Box>
  </Box>
);

// ─── MonthNav ─────────────────────────────────────────────────────────────────
export const MonthNav = ({ title, onPrev, onNext }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <IconButton size="small" onClick={onPrev} sx={{
      width: 30, height: 30, borderRadius: R.sm,
      border: `1px solid ${C.border}`, bgcolor: alpha(C.bg3, 0.5),
      color: C.textSecondary, transition: TRANSITION,
      '&:hover': { color: C.textPrimary, borderColor: C.border2, bgcolor: alpha(C.bg3, 0.9) },
    }}>
      <ChevronLeftIcon sx={{ fontSize: 18 }} />
    </IconButton>
    <Typography sx={{
      fontSize: 13, fontWeight: 700, color: C.textPrimary, fontFamily: FONT_UI,
      minWidth: 112, textAlign: 'center', px: 0.5,
    }}>
      {title}
    </Typography>
    <IconButton size="small" onClick={onNext} sx={{
      width: 30, height: 30, borderRadius: R.sm,
      border: `1px solid ${C.border}`, bgcolor: alpha(C.bg3, 0.5),
      color: C.textSecondary, transition: TRANSITION,
      '&:hover': { color: C.textPrimary, borderColor: C.border2, bgcolor: alpha(C.bg3, 0.9) },
    }}>
      <ChevronRightIcon sx={{ fontSize: 18 }} />
    </IconButton>
  </Box>
);

// ─── StatTile — compact metric card ──────────────────────────────────────────
export const StatTile = ({ icon: Icon, label, value, accent = C.blue }) => (
  <Box sx={{
    borderRadius: R.lg, p: '14px 16px',
    background: alpha(C.bg2, 0.6),
    border: `1px solid ${C.border}`,
    display: 'flex', flexDirection: 'column', gap: 0.75,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {Icon && (
        <Box sx={{
          width: 28, height: 28, borderRadius: R.sm,
          bgcolor: alpha(accent, 0.1), border: `1px solid ${alpha(accent, 0.2)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon sx={{ fontSize: 14, color: accent }} />
        </Box>
      )}
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.textSecondary, fontFamily: FONT_UI, textTransform: 'uppercase', letterSpacing: 0.7 }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: 22, fontWeight: 800, color: C.textPrimary, fontFamily: FONT_TITLE, lineHeight: 1 }}>
      {value}
    </Typography>
  </Box>
);

// ─── Stat grid (4 tiles) — daily / monthly panels ────────────────────────────
export const AttendanceStatGrid = ({ tiles }) => (
  <Box sx={{ px: 3, pt: 2.5, pb: 2, display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
    {tiles.map((t, i) => (
      <StatTile key={t.key ?? i} icon={t.icon} label={t.label} value={t.value} accent={t.accent} />
    ))}
  </Box>
);

// ─── Search + department row — shared by daily / monthly ──────────────────────
export const AttendanceSearchDepartmentRow = ({
  searchTerm, onSearchChange, departmentFilter, onDepartmentChange, departments,
}) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
    <TextField
      placeholder="Search by name…" size="small"
      value={searchTerm}
      onChange={onSearchChange}
      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: C.textSecondary }} /></InputAdornment> }}
      sx={{ ...inputSx, flex: '1 1 200px', minWidth: 200, maxWidth: 300 }}
    />
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <Select
        value={departmentFilter} displayEmpty
        onChange={onDepartmentChange}
        sx={selectSx}
        renderValue={(v) => (v === '' ? <span style={{ color: C.textSecondary }}>All departments</span> : departments.find(([id]) => id === v)?.[1])}
      >
        <MenuItem value="">All departments</MenuItem>
        {departments.map(([id, name]) => (
          <MenuItem key={id} value={id}>{name}</MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
);

// ─── Labeled chip row — status (daily) / legend (monthly) ─────────────────────
export const AttendanceFilterChipRow = ({ sectionLabel, chips }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.textSecondary, fontFamily: FONT_UI, mr: 0.5 }}>
      {sectionLabel}
    </Typography>
    {chips.map(({ key, label, color, selected, onClick }) => (
      <FilterChip key={key} label={label} color={color} selected={selected} onClick={onClick} />
    ))}
  </Box>
);

// ─── Wrapper: search row + chip row (optional bottom padding) ─────────────────
export const AttendancePanelFilters = ({ children, sx = {} }) => (
  <Box sx={{ px: 3, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5, ...sx }}>
    {children}
  </Box>
);

// ─── FilterChip ───────────────────────────────────────────────────────────────
export const FilterChip = ({ label, selected, color = C.blue, onClick }) => (
  <Chip
    clickable={typeof onClick === 'function'}
    size="small"
    onClick={onClick}
    label={label}
    sx={{
      height: 28, borderRadius: 999, fontFamily: FONT_UI, fontWeight: 600, fontSize: 12,
      border: `1px solid ${selected ? alpha(color, 0.35) : C.border}`,
      bgcolor: selected ? alpha(color, 0.12) : alpha(C.bg2, 0.5),
      color: selected ? color : C.textSecondary,
      transition: TRANSITION,
      '& .MuiChip-label': { px: 1.5 },
      '&:hover': { bgcolor: selected ? alpha(color, 0.18) : alpha(C.bg2, 0.9), borderColor: alpha(color, 0.3), color: selected ? color : C.textPrimary },
    }}
  />
);

// ─── DrawerInfoRow ────────────────────────────────────────────────────────────
export const DrawerInfoRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
    <Typography sx={{ fontSize: 12.5, color: C.textSecondary, fontFamily: FONT_UI }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, color: C.textPrimary, fontFamily: FONT_MONO, fontWeight: 500, letterSpacing: 0.3 }}>{value}</Typography>
  </Box>
);

// ─── DrawerBadge ─────────────────────────────────────────────────────────────
export const DrawerBadge = ({ label, value, color = C.blue }) => (
  <Box sx={{
    borderRadius: R.md, p: '10px 14px',
    border: `1px solid ${alpha(color, 0.2)}`,
    bgcolor: alpha(color, 0.07),
  }}>
    <Typography sx={{ fontSize: 11, color: C.textSecondary, fontFamily: FONT_UI, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</Typography>
    <Typography sx={{ fontSize: 20, color: C.textPrimary, fontFamily: FONT_TITLE, fontWeight: 800, mt: 0.25 }}>{value}</Typography>
  </Box>
);

// ─── Input field sx factory ───────────────────────────────────────────────────
export const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: R.md, fontSize: 13, fontFamily: FONT_UI, fontWeight: 500,
    color: C.textPrimary, bgcolor: alpha(C.bg2, 0.5), transition: TRANSITION,
    '& fieldset': { borderColor: C.border },
    '&:hover fieldset': { borderColor: C.border2 },
    '&.Mui-focused fieldset': { borderColor: alpha(C.blue, 0.5) },
  },
  '& .MuiInputLabel-root': { color: C.textSecondary, fontSize: 13, fontFamily: FONT_UI },
  '& .MuiInputBase-input': { py: 1.1 },
};

// ─── Select sx ────────────────────────────────────────────────────────────────
export const selectSx = {
  borderRadius: R.md, fontSize: 13, fontFamily: FONT_UI,
  color: C.textPrimary, bgcolor: alpha(C.bg2, 0.5), transition: TRANSITION,
  '& .MuiOutlinedInput-notchedOutline':             { borderColor: C.border },
  '&:hover .MuiOutlinedInput-notchedOutline':       { borderColor: C.border2 },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: alpha(C.blue, 0.5) },
  '& .MuiSvgIcon-root': { color: C.textSecondary },
};

// ─── Table shell (matches TableList) ─────────────────────────────────────────
const MotionBox = motion(Box);

const tableStreamLabelSx = {
  fontFamily: "'DM Mono', 'Fira Mono', monospace",
  fontSize: '0.6rem',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: alpha(TEXT_PRIMARY, 0.65),
  lineHeight: 1,
};

export const AttendanceTableShell = ({
  streamLabel,
  subtitle,
  recordCount,
  headerRight = null,
  children,
}) => (
  <MotionBox
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.2 }}
    sx={tableListShellSx}
  >
    <Box sx={tableListHeaderBarSx}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Box sx={{ position: 'relative', width: 10, height: 10, flexShrink: 0 }}>
            <Box sx={{
              width: 6, height: 6, borderRadius: '50%', background: ACCENT,
              boxShadow: `0 0 7px ${alpha(ACCENT, 0.9)}`,
            }} />
          </Box>
          <Typography sx={tableStreamLabelSx}>{streamLabel}</Typography>
        </Box>
        {subtitle && (
          <Typography sx={{ fontSize: 11.5, color: alpha(TEXT_SECONDARY, 0.9), fontFamily: FONT_UI, pl: 2.6 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {recordCount != null && (
          <Box sx={{
            px: 1.2, py: 0.4, borderRadius: '7px',
            background: alpha(ACCENT, 0.07),
            border: `1px solid ${alpha(ACCENT, 0.18)}`,
          }}>
            <Typography sx={{ ...tableStreamLabelSx, color: alpha(ACCENT, 0.75), fontSize: '0.57rem' }}>
              {recordCount} records
            </Typography>
          </Box>
        )}
        {headerRight}
      </Box>
    </Box>
    <Box sx={{ position: 'relative', zIndex: 1 }}>{children}</Box>
  </MotionBox>
);

/** Click-cycle guide for HR and team leaders (monthly grid cells). */
const STEPS = [
  {
    click:   '1st click',
    label:   'Present',
    sub:     'Marked present',
    color:   '#10b981',
    symbol:  '✓',
  },
  {
    click:   '2nd click',
    label:   'Absent w/ approval',
    sub:     'Approved absence',
    color:   '#f59e0b',
    symbol:  '~',
  },
  {
    click:   '3rd click',
    label:   'Absent',
    sub:     'Unapproved absence',
    color:   '#ef4444',
    symbol:  '✕',
  },
  {
    click:   '4th click',
    label:   'Reset',
    sub:     'Cycles back to start',
    color:   '#60a5fa',
    symbol:  '↺',
  },
];

// ─── AttendanceStatusLegend ────────────────────────────────────────────────────
export const AttendanceStatusLegend = ({ visible = true }) => {
  if (!visible) return null;

  return (
    <Box sx={{
      mt: 0,
      mx: 0,
      borderTop: '1px solid rgba(255,255,255,0.06)',
      pt: 2,
      pb: 0.5,
    }}>
      {/* Header row */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.75,
      }}>
        {/* Icon */}
        <Box sx={{
          width: 28, height: 28,
          borderRadius: '8px',
          bgcolor: alpha('#60a5fa', 0.1),
          border: `1px solid ${alpha('#60a5fa', 0.22)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Typography sx={{ fontSize: 13, lineHeight: 1, color: '#60a5fa' }}>⊕</Typography>
        </Box>

        <Box>
          <Typography sx={{
            fontSize: 12.5, fontWeight: 700, color: '#cbd5e1',
            fontFamily: FONT_UI, lineHeight: 1.2,
          }}>
            Team Leaders & HR — monthly cell clicks
          </Typography>
          <Typography sx={{
            fontSize: 11, color: 'rgba(100,116,139,0.8)',
            fontFamily: FONT_UI, mt: 0.2,
          }}>
            Click any day cell to cycle its attendance status
          </Typography>
        </Box>
      </Box>

      {/* Step cards */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 1,
      }}>
        {STEPS.map((step, i) => (
          <Box
            key={step.click}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              px: 1.5,
              py: 1.25,
              borderRadius: '10px',
              bgcolor: alpha(step.color, 0.07),
              border: `1px solid ${alpha(step.color, 0.2)}`,
              overflow: 'hidden',
              transition: 'border-color 0.15s',
              '&:hover': {
                borderColor: alpha(step.color, 0.38),
                bgcolor: alpha(step.color, 0.1),
              },
            }}
          >
            {/* Step number tag */}
            <Box sx={{
              position: 'absolute',
              top: 8, right: 10,
              fontSize: 9, fontWeight: 800,
              letterSpacing: '0.7px',
              fontFamily: FONT_UI,
              color: alpha(step.color, 0.55),
              textTransform: 'uppercase',
            }}>
              {step.click}
            </Box>

            {/* Symbol dot */}
            <Box sx={{
              width: 28, height: 28,
              borderRadius: '8px',
              bgcolor: alpha(step.color, 0.15),
              border: `1.5px solid ${alpha(step.color, 0.4)}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              color: step.color,
              fontSize: 13, fontWeight: 800,
              fontFamily: FONT_MONO,
              mt: 0.25,
            }}>
              {step.symbol}
            </Box>

            {/* Text */}
            <Box sx={{ pt: 0.15 }}>
              <Typography sx={{
                fontSize: 12.5, fontWeight: 700, color: '#f1f5f9',
                fontFamily: FONT_UI, lineHeight: 1.2, mb: 0.35,
                pr: 4,
              }}>
                {step.label}
              </Typography>
              <Typography sx={{
                fontSize: 10.5, color: alpha(step.color, 0.75),
                fontFamily: FONT_UI, lineHeight: 1.3,
              }}>
                {step.sub}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── Global CSS injection (call once at app level) ────────────────────────────
export const ATT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500&family=Rajdhani:wght@600;700&family=DM+Sans:opsz,wght@9..40,300;400;500;600;700&display=swap');
`;